import { useEffect, useRef } from 'react';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { sendEmbeddedSignupEvents } from 'src/lib/facebook/embeddedSignupEvents.service';
import {
  installEmbeddedSignupTelemetry,
  registerEmbeddedSignupSink,
  type tEmbeddedSignupRecord,
} from 'src/lib/facebook/embeddedSignupTelemetry';

// Registry keys. Every surface registers the same keyed sinks, so mounting this
// hook on two surfaces at once still emits one event per record.
// See registerEmbeddedSignupSink.
const POSTHOG_SINK_KEY = 'posthog';
const HTTP_SINK_KEY = 'http';

/**
 * Emits Embedded Signup abandonment / error records to PostHog and to our own
 * backend (INT-1216).
 *
 * Mount this wherever the popup can be launched. Both the listener install and
 * the sink registration are idempotent, so multiple mounts are safe — which
 * matters because the Dashboard route mounts the connection flow twice.
 *
 * The launch surface travels on the attempt (see `beginEmbeddedSignupAttempt`),
 * not on this hook, so the sinks need no knowledge of where they were mounted.
 *
 * Registration happens ONCE per mount and reads its dependencies through a ref.
 * `trackWapiEvent` is a fresh function on every render of `useWapiAnalytics`, so
 * depending on it directly would re-register both sinks on every render of a
 * component as busy as the Dashboard.
 */
export function useEmbeddedSignupTelemetry() {
  const { trackWapiEvent } = useWapiAnalytics();
  const { currentWorkspaceId } = useWorkspaceContext();

  const latest = useRef({ trackWapiEvent, currentWorkspaceId });
  latest.current = { trackWapiEvent, currentWorkspaceId };

  useEffect(() => {
    installEmbeddedSignupTelemetry();

    function toPostHog(record: tEmbeddedSignupRecord) {
      const shared = {
        surface: record.surface,
        // Our own id, not Meta's session_id — abandonment events carry no Meta
        // identifier at all, so this is the only join key that always exists.
        signup_attempt_id: record.attemptId,
        signup_attempt_uncorrelated: record.orphaned,
        meta_event: record.event,
        esu_version: record.version,
        // Absent rather than null when the launch declared no path, matching the
        // wizard's connection events.
        ...(record.path ? { path: record.path } : {}),
      };

      if (record.kind === 'abandoned') {
        latest.current.trackWapiEvent(
          WAPI_EVENTS.INTEGRATION_CONNECTION_SIGNUP_ABANDONED,
          {
            ...shared,
            // Raw string. Meta ships steps it has not documented, so this must
            // never be narrowed to a known set.
            current_step: record.currentStep,
          },
        );
        return;
      }

      latest.current.trackWapiEvent(
        WAPI_EVENTS.INTEGRATION_CONNECTION_SIGNUP_ERROR_REPORTED,
        {
          ...shared,
          error_code: record.errorCode,
          error_message: record.errorMessage,
          meta_session_id: record.metaSessionId,
          meta_timestamp: record.metaTimestamp,
        },
      );
    }

    function toBackend(record: tEmbeddedSignupRecord) {
      // Fire-and-forget by design: the durable write must never block, throw into
      // Meta's message dispatch, or affect connection state. Failures are
      // swallowed inside the service.
      void sendEmbeddedSignupEvents(latest.current.currentWorkspaceId ?? '', [record]);
    }

    const unregisterPostHog = registerEmbeddedSignupSink(toPostHog, POSTHOG_SINK_KEY);
    const unregisterBackend = registerEmbeddedSignupSink(toBackend, HTTP_SINK_KEY);

    return () => {
      unregisterPostHog();
      unregisterBackend();
    };
  }, []);
}
